import {Tab, Tabs} from '@mui/material';
import {ConfigObjectType, ENABLE_PH_REC, LightLayoutNodeFCProps, MaximizeButton, MinimizeRightButton} from '@wallaby-ui/core';
import {MerieSchema, RiskFactorLabelEnum, RiskFactorTypeEnum, TableSorting, useFormatters} from '@wallaby-ui/document';
import React from 'react';

import {HospitalizationState} from './HospitalizationDetailLayoutV2';
import {HospitalizationRiskFactorsV2} from './HospitalizationDetailRiskFactorsV2';
import {HospitalizationPhRecommendationPanelV2} from './HospitalizationPhRecommendationPanelV2';

export const DEFAULT_RISK_FACTORS_SORTING: TableSorting = {
  field: 'date',
  direction: 'desc',
};
export type HospitalizationRightPaneProps = {
  state: HospitalizationState;
  changeState: (diff: Partial<HospitalizationState>) => void;
  storageIdPrefix: string;
  config: ConfigObjectType;
  hospitalizationId: string;
  defaultDepartmentId: number | null;
  riskFactorToRiskFactorLabel: Record<RiskFactorTypeEnum, RiskFactorLabelEnum>;
  patientCardLoading: boolean;
  riskFactors: MerieSchema.components['schemas']['RiskFactorDto'][];
  riskFactorsEtag: string | undefined;
  oldRiskFactorIds: Set<number>;
  reloadRiskFactors: () => void;
  setTimelineDefaultScroll: (v: number) => void;
  pharmaRecommendationsLoading: boolean;
  pharmaRecommendations: MerieSchema.components['schemas']['PharmaRecommendationsDto'] | undefined;
  pharmaRecommendationsRefetch: () => void;
  reloadTimeline: () => void;
};
export const HospitalizationRightPaneV2: React.FC<LightLayoutNodeFCProps & HospitalizationRightPaneProps> = (props) => {
  const {
    state,
    changeState,
    storageIdPrefix,
    config,
    hospitalizationId,
    defaultDepartmentId,
    riskFactorToRiskFactorLabel,
    patientCardLoading,
    riskFactors,
    riskFactorsEtag,
    oldRiskFactorIds,
    reloadRiskFactors,
    setTimelineDefaultScroll,
    minimized,
    maximized,
    setMinimizedMaximized,
    pharmaRecommendations,
    pharmaRecommendationsLoading,
    pharmaRecommendationsRefetch,
    reloadTimeline,
  } = props;
  const selectedTab = state.rightTabs ?? 0;
  const setSelectedTab = (newValue: number) => changeState({rightTabs: newValue});
  const {t} = useFormatters();
  const isBigMode = !minimized || maximized;
  return (
    <div className='dl-light-box' data-page-box='true' data-fill-available={isBigMode}>
      <div className='dl-header-buttons-row-wide' data-rotate={isBigMode ? '' : 'right'}>
        <Tabs style={{flex: 1, paddingRight: 8}} className='dl-tab-row' value={selectedTab}>
          <Tab
            label={t('hospitalization.detailV2.riskFactorsTab')}
            onClick={() => {
              setSelectedTab(0);
              setMinimizedMaximized({minimized: false});
            }}
          />
          {ENABLE_PH_REC && (
            <Tab
              label={t('hospitalization.detailV2.phRecommendationTab')}
              onClick={() => {
                setSelectedTab(1);
                setMinimizedMaximized({minimized: false});
              }}
            />
          )}
        </Tabs>
        <div className='dl-header-icons-row'>
          <MinimizeRightButton
            minimized={minimized}
            toggleMinimized={() => setMinimizedMaximized({minimized: !minimized, maximized: false})}
          />
          <MaximizeButton maximized={maximized} toggleMaximized={() => setMinimizedMaximized({maximized: !maximized})} tall />
        </div>
      </div>
      {isBigMode && (
        <>
          {selectedTab === 0 && (
            <HospitalizationRiskFactorsV2
              state={state}
              changeState={changeState}
              storageIdPrefix={storageIdPrefix}
              config={config}
              hospitalizationId={hospitalizationId}
              riskFactors={riskFactors}
              riskFactorsEtag={riskFactorsEtag}
              oldRiskFactorIds={oldRiskFactorIds}
              reloadRiskFactors={reloadRiskFactors}
              setTimelineDefaultScroll={setTimelineDefaultScroll}
              setMinimizedMaximized={setMinimizedMaximized}
            />
          )}
          {selectedTab === 1 && ENABLE_PH_REC && (
            <HospitalizationPhRecommendationPanelV2
              state={state}
              changeState={changeState}
              storageIdPrefix={storageIdPrefix}
              config={config}
              hospitalizationId={hospitalizationId}
              defaultDepartmentId={defaultDepartmentId}
              riskFactorToRiskFactorLabel={riskFactorToRiskFactorLabel}
              patientCardLoading={patientCardLoading}
              encounterRiskFactors={riskFactors}
              pharmaRecommendationsLoading={pharmaRecommendationsLoading}
              pharmaRecommendations={pharmaRecommendations}
              pharmaRecommendationsRefetch={pharmaRecommendationsRefetch}
              reloadTimeline={reloadTimeline}
            />
          )}
        </>
      )}
    </div>
  );
};
